import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_FILE_BY_PUBLIC_ID } from "@feature/file/service/useGetLazyByPublicId/graphQL";
import { IGetFileResponse } from "@feature/file/service/useGetLazyByPublicId/type";
import GENERATE_SAS_TOKEN from "@graphql/mutations/generateSasToken";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "../../store/RootState";
import "./DocumentEditor.scss";

const DocumentEditor: React.FC = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const documentId = queryParams.get("id") || "";
  const [file, setFile] = useState<IGetFileResponse>();
  const decodedDocument = atob(documentId);
  const fileType = "docx";
  const [generateSasToken] = useMutation(GENERATE_SAS_TOKEN);
  const { publicId, fullName, email } = useSelector((state: RootState) => ({
    publicId: state.user.userPublicId,
    fullName: state.user.fullName,
    email: state.user.email,
  }));
  const [fileDirectory, fileName, filePublicId, collectionPublicId] =
    decodedDocument.split("/");
  const [getFile] = useLazyQuery<IGetFileResponse>(GET_FILE_BY_PUBLIC_ID, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    const handlePageHide = (event: PageTransitionEvent) => {
      const isReload = event.persisted;
      if (isReload) return;

      const bc = new BroadcastChannel("docEditChannel");
      bc.postMessage({
        type: "doc-updated",
        filePublicId,
        collectionId: collectionPublicId,
        onlyOfficeEditorKey: file?.fileInfo?.onlyOfficeEditorKey,
      });
      bc.close();
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [filePublicId, collectionPublicId, file?.fileInfo?.onlyOfficeEditorKey]);

  useEffect(() => {
    const loadScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if ((window as any).DocsAPI) return resolve();

        const script = document.createElement("script");
        script.src = `${process.env.REACT_APP_ONLY_OFFICE_DOCKER_URL}/web-apps/apps/api/documents/api.js`;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("OnlyOffice script load failed"));
        document.body.appendChild(script);
      });
    };

    const setupEditor = async () => {
      try {
        const { data } = await getFile({ variables: { publicId: filePublicId } });

        if (!data || !data.fileInfo) {
          throw new Error("File not found or file info is missing");
        } else {
          setFile(data);

          await loadScript();

          const payload = {
            documentType: "word",
            document: {
              title: fileName,
              url: `${
                process.env.REACT_APP_BASE_URL
              }/api/backend-onlyoffice/url?fileName=${encodeURIComponent(
                fileName
              )}&fileDirectory=${encodeURIComponent(fileDirectory)}`,
              fileType,
              key: data?.fileInfo?.onlyOfficeEditorKey,
            },
            permissions: {
              chat: true,
            },
            editorConfig: {
              mode: "edit",
              lang: "en",
              callbackUrl: `${
                process.env.REACT_APP_BASE_URL
              }/api/backend-onlyoffice/callback?fileName=${encodeURIComponent(
                fileName
              )}&fileDirectory=${encodeURIComponent(
                fileDirectory
              )}&filePublicId=${encodeURIComponent(
                filePublicId
              )}&email=${encodeURIComponent(email)}`,
              coEditing: {
                mode: "fast",
                change: true,
              },
              user: {
                id: publicId,
                name: fullName,
              },
              customization: {
                comments: true,
                compactToolbar: false,
                collaborativeEditing: true,
                review: true,
                forcesave: true,
              },
            },
          };

          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/api/backend/onlyOffice/jwt`,
            payload
          );

          const { jwtToken } = response.data;

          const config = {
            documentType: "word",
            token: jwtToken,
            document: {
              title: fileName,
              url: `${
                process.env.REACT_APP_BASE_URL
              }/api/backend-onlyoffice/url?fileName=${encodeURIComponent(
                fileName
              )}&fileDirectory=${encodeURIComponent(fileDirectory)}`,
              fileType,
              key: data?.fileInfo?.onlyOfficeEditorKey,
            },
            permissions: {
              chat: true,
            },
            editorConfig: {
              mode: "edit",
              lang: "en",
              callbackUrl: `${
                process.env.REACT_APP_BASE_URL
              }/api/backend-onlyoffice/callback?fileName=${encodeURIComponent(
                fileName
              )}&fileDirectory=${encodeURIComponent(
                fileDirectory
              )}&filePublicId=${encodeURIComponent(
                filePublicId
              )}&email=${encodeURIComponent(email)}`,
              coEditing: {
                mode: "fast",
                change: true,
              },
              user: {
                id: publicId,
                name: fullName,
              },
              customization: {
                comments: true,
                compactToolbar: false,
                collaborativeEditing: true,
                review: true,
                forcesave: true,
              },
            },
          };

          (window as any).docEditor = new (window as any).DocsAPI.DocEditor(
            "docEditor",
            config
          );
        }
      } catch (error) {
        console.error("Error setting up the editor:", error);
      }
    };

    setupEditor();
  }, [
    publicId,
    fullName,
    fileDirectory,
    fileName,
    filePublicId,
    getFile,
    file?.fileInfo?.onlyOfficeEditorKey,
    generateSasToken,
  ]);

  return <div id="docEditor"></div>;
};

export default DocumentEditor;
